/**
 * Shows the current user's outbound borrow requests that need attention:
 * all pending requests and rejected requests from the last 7 days.
 *
 * Older rejections are filtered out to keep the list uncluttered — the user
 * has likely already seen and acknowledged them by that point.
 * Returns null when there is nothing actionable to show.
 */
import {
  useMyLoanRequests,
  useCancelRequest,
  useDismissRejectedRequest,
} from '@/hooks/useLoanRequests';
import { useNetworkStatusContext } from '@/contexts/NetworkStatusContext';
import { toast } from 'sonner';
import { X, AlertTriangle, Check } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { T, RADIUS, SHADOW } from '@/lib/tokens';

/**
 *
 */
export function MyLoanRequests() {
  const { data: requests, isLoading, isError, refetch } = useMyLoanRequests();
  const cancelRequest = useCancelRequest();
  const dismissRequest = useDismissRejectedRequest();
  const { isOnline } = useNetworkStatusContext();

  const handleCancel = async (requestId: string) => {
    try {
      await cancelRequest.mutateAsync(requestId);
      toast.success('Request cancelled');
    } catch {
      // Error toast handled by useCancelRequest onError
    }
  };

  const handleDismiss = async (requestId: string) => {
    try {
      await dismissRequest.mutateAsync(requestId);
      toast.success('Request dismissed');
    } catch {
      // Error toast handled by useDismissRejectedRequest onError
    }
  };

  if (isLoading) return null;

  if (isError) {
    return (
      <div style={{ fontSize: 14, color: T.danger, padding: '8px 0' }}>
        Failed to load your requests.{' '}
        <button
          onClick={() => refetch()}
          style={{
            background: 'none',
            border: 'none',
            color: T.accent,
            cursor: 'pointer',
            fontFamily: 'inherit',
            fontSize: 14,
            fontWeight: 600,
            padding: 0,
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  const sevenDaysAgo = new Date();
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

  // Include pending requests always; include rejections only for the past 7 days
  // so stale rejections don't clutter the list indefinitely.
  const relevantRequests = requests?.filter(
    (r) =>
      r.status === 'pending' ||
      (r.status === 'rejected' && new Date(r.responded_at || r.created_at) > sevenDaysAgo),
  );

  if (!relevantRequests || relevantRequests.length === 0) return null;

  return (
    <div
      style={{
        background: T.surface,
        border: `1px solid ${T.border}`,
        borderRadius: RADIUS.lg,
        boxShadow: SHADOW.md,
        marginBottom: 16,
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: '14px 16px 12px',
          borderBottom: `1px solid ${T.border}`,
        }}
      >
        <div style={{ fontSize: 15, fontWeight: 600, color: T.text, margin: '0 0 2px' }}>
          Your Borrow Requests
        </div>
        <div style={{ fontSize: 13, color: T.textMuted, margin: 0 }}>
          Track the status of items you've requested to borrow
        </div>
      </div>

      {/* Request list */}
      <div style={{ padding: '12px 16px 16px' }}>
        {relevantRequests.map((request, idx) => (
          <div
            key={request.id}
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: 12,
              padding: 12,
              border: `1px solid ${T.border}`,
              borderRadius: RADIUS.sm,
              marginBottom: idx < relevantRequests.length - 1 ? 8 : 0,
            }}
          >
            <div style={{ flex: 1 }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  flexWrap: 'wrap',
                  marginBottom: 4,
                }}
              >
                <span style={{ fontSize: 14, fontWeight: 600, color: T.text }}>
                  {request.item_name}
                </span>
                {/* Status badge */}
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    padding: '2px 8px',
                    borderRadius: RADIUS.dot,
                    letterSpacing: '0.03em',
                    background: request.status === 'rejected' ? T.dangerSoft : T.surfaceSubtle,
                    color: request.status === 'rejected' ? T.danger : T.textSec,
                    border: `1px solid ${request.status === 'rejected' ? T.dangerBorder : T.border}`,
                  }}
                >
                  {request.status}
                </span>
              </div>
              <div
                style={{
                  fontSize: 13,
                  color: T.textMuted,
                  marginBottom: request.status === 'rejected' && request.rejection_reason ? 8 : 0,
                }}
              >
                Requested from {request.approver_name} ·{' '}
                {formatDistanceToNow(new Date(request.created_at), { addSuffix: true })}
              </div>
              {request.status === 'rejected' && request.rejection_reason && (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 8,
                    marginTop: 8,
                    padding: '8px 10px',
                    borderRadius: RADIUS.sm,
                    background: T.warningSoft,
                    border: `1px solid ${T.warningBorder}`,
                    color: T.warning,
                    fontSize: 13,
                  }}
                >
                  <AlertTriangle size={14} style={{ marginTop: 1, flexShrink: 0 }} />
                  <span>{request.rejection_reason}</span>
                </div>
              )}
            </div>
            {request.status === 'pending' && (
              <button
                onClick={() => handleCancel(request.id)}
                disabled={cancelRequest.isPending || !isOnline}
                aria-label="Cancel request"
                title={!isOnline ? "You're offline" : undefined}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 32,
                  height: 32,
                  borderRadius: RADIUS.sm,
                  border: `1px solid ${T.border}`,
                  background: T.surface,
                  cursor: cancelRequest.isPending || !isOnline ? 'not-allowed' : 'pointer',
                  color: T.textSec,
                  flexShrink: 0,
                  opacity: cancelRequest.isPending || !isOnline ? 0.6 : 1,
                }}
              >
                <X size={14} />
              </button>
            )}
            {request.status === 'rejected' && (
              <button
                onClick={() => handleDismiss(request.id)}
                disabled={dismissRequest.isPending || !isOnline}
                aria-label="Dismiss"
                title={!isOnline ? "You're offline" : undefined}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 32,
                  height: 32,
                  borderRadius: RADIUS.sm,
                  border: `1px solid ${T.border}`,
                  background: T.surface,
                  cursor: dismissRequest.isPending || !isOnline ? 'not-allowed' : 'pointer',
                  color: T.textSec,
                  flexShrink: 0,
                  opacity: dismissRequest.isPending || !isOnline ? 0.6 : 1,
                }}
              >
                <Check size={14} />
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
